import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LucideProps, Calendar, MessageCircle, Phone, CreditCard, Paperclip } from 'lucide-react';

// --- TYPE DEFINITIONS ---

export const ACTION_TYPES = {
  SCHEDULE: 'schedule',
  MESSAGE: 'message',
  CALL: 'call',
  PAYMENT: 'payment',
  ATTACHMENT: 'attachment',
} as const;

export type ActionType = typeof ACTION_TYPES[keyof typeof ACTION_TYPES];

export interface Action {
  id: string;
  label: string;
  icon: React.ComponentType<LucideProps>;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | null;
}

export const DEFAULT_ACTIONS: Action[] = [
  { id: ACTION_TYPES.SCHEDULE, label: 'Schedule', icon: Calendar, variant: 'default' },
  { id: ACTION_TYPES.MESSAGE, label: 'Message', icon: MessageCircle, variant: 'default' },
  { id: ACTION_TYPES.CALL, label: 'Call', icon: Phone, variant: 'default' },
  { id: ACTION_TYPES.PAYMENT, label: 'Payment', icon: CreditCard, variant: 'default' },
  { id: ACTION_TYPES.ATTACHMENT, label: 'Attachment', icon: Paperclip, variant: 'default' },
];

// --- SUB-COMPONENTS ---

interface ProfileActionItemProps {
  action: Action;
  disabled?: boolean;
  selected?: boolean;
  onClick?: (action: Action) => void;
  className?: string;
}

const ProfileActionItem = React.forwardRef<HTMLButtonElement, ProfileActionItemProps>((
  { action, disabled = false, selected = false, onClick, className = '' },
  ref
) => {
  if (!action || !action.id || !action.label || !action.icon) {
    console.warn('ProfileActionItem: action object with id, label, and icon is required');
    return null;
  }

  const IconComponent = action.icon;

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(action);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      if (onClick) onClick(action);
    }
  };

  return (
    <Button
      ref={ref}
      variant="ghost"
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-4 py-3 rounded-lg h-14',
        'text-xs font-semibold',
        {
          'bg-slate-100 text-slate-900': selected,
          'hover:bg-slate-50': !selected && !disabled,
          'opacity-50 cursor-not-allowed': disabled,
        },
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={action.label}
    >
      <IconComponent className="h-5 w-5" />
      <span>{action.label}</span>
    </Button>
  );
});
ProfileActionItem.displayName = 'ProfileActionItem';

// --- MAIN COMPONENT ---

export interface ProfileActionCardProps {
  actions?: Action[];
  disabledActions?: string[];
  selectedActions?: string[];
  onActionClick?: (action: Action) => void;
  responsive?: boolean;
  className?: string;
}

const ProfileActionCard = React.forwardRef<HTMLDivElement, ProfileActionCardProps>((
  { 
    actions = DEFAULT_ACTIONS,
    disabledActions = [],
    selectedActions = [],
    onActionClick, 
    responsive = true, 
    className = ''
  },
  ref
) => {
  if (!Array.isArray(actions) || actions.length === 0) {
    return null;
  }

  return (
    <div 
      ref={ref}
      className={cn(
        'bg-white rounded-xl p-3 shadow-sm border border-gray-200 w-full',
        className
      )}
      role="toolbar"
      aria-label="Profile actions"
    >
      <div 
        className={cn(
          'flex gap-2',
          responsive && 'flex-wrap sm:flex-nowrap'
        )}
      >
        {actions.map((action) => (
          <ProfileActionItem
            key={action.id}
            action={action}
            disabled={disabledActions.includes(action.id)}
            selected={selectedActions.includes(action.id)}
            onClick={onActionClick}
            className={responsive ? 'min-w-[70px] max-w-[120px]' : 'min-w-[90px]'}
          />
        ))}
      </div>
    </div>
  );
});
ProfileActionCard.displayName = 'ProfileActionCard';

export default ProfileActionCard;
